<template>
  <BaseDialog :value="true" max-width="700" persistent>
    <template #modal>
      <BaseCosmoCard>
        <template #title>
          <div class="modal_header">
            <BaseTitle
              class="modal_header_title"
              v-text="$t('folder.yousign.relaunchMessage')"
            />
            <BaseButtonIcon icon="$mdiClose" @click="close()" />
          </div>
        </template>
        <div class="modal_content">
          <div class="item_section mb-4">
            <div class="section_header">
              <div class="label" v-text="$t('utils.selectContactsToNotify')" />
              <v-btn
                v-if="!isFetchingContacts && allContacts.length"
                text
                x-small
                color="primary"
                @click="toggleAll"
              >
                {{ $t(allSelected ? "utils.deselectAll" : "utils.selectAll") }}
              </v-btn>
            </div>
            <div class="desc">
              <div v-if="isFetchingContacts" class="recipients_loading">
                <v-progress-circular indeterminate size="20" width="2" />
              </div>
              <template v-else>
                <v-checkbox
                  v-for="contact in allContacts"
                  :key="contact.id"
                  v-model="selectedRecipients"
                  :value="contact.id"
                  hide-details
                  dense
                >
                  <template #label>
                    <div class="contact_label">
                      <div class="contact_identity">
                        <span v-if="contact.firstname || contact.lastname" class="contact_name">
                          {{ [contact.firstname, contact.lastname].filter(Boolean).join(" ") }}
                        </span>
                        <span class="contact_email">{{ contact.email }}</span>
                      </div>
                      <div v-if="contact.contexts && contact.contexts.length" class="contact_contexts">
                        <span
                          v-for="ctx in contact.contexts"
                          :key="ctx.label"
                          class="contact_context_badge"
                        >{{ ctx.label }}</span>
                      </div>
                    </div>
                  </template>
                </v-checkbox>
              </template>
            </div>
          </div>
          <BaseMarkdownEditor
            v-model="message"
            :label="$t('folder.yousign.relaunchMessage')"
          />
        </div>

        <div class="modal_footer">
          <BaseButton color="grey" type="secondary" @click="close()">
            {{ $t("utils.cancel") }}
          </BaseButton>
          <BaseButton
            :loading="isSubmiting"
            :disabled="isSubmiting || selectedRecipients.length === 0"
            @click="relaunchFolder()"
          >
            {{ $t("utils.validate") }}
          </BaseButton>
        </div>
      </BaseCosmoCard>
    </template>
  </BaseDialog>
</template>

<script>
import {
  getFolderContacts,
  postFolderRemind,
} from "@/modules/Folder/Services/folder.service";
export default {
  name: "FolderRelaunchDialog",
  data() {
    return {
      isFetchingContacts: false,
      isSubmiting: false,
      message: "",
      owner: null,
      thirdParties: [],
      selectedRecipients: [],
    };
  },
  computed: {
    allContacts() {
      return this.owner
        ? [this.owner, ...this.thirdParties]
        : this.thirdParties;
    },
    allSelected() {
      return (
        this.allContacts.length > 0 &&
        this.selectedRecipients.length === this.allContacts.length
      );
    },
  },
  created() {
    this.fetchContacts();
  },
  methods: {
    async fetchContacts() {
      this.isFetchingContacts = true;
      try {
        const { data } = await getFolderContacts({
          organizationId: this.$route.params.organizationId,
          folderId: this.$route.params.folderId,
        });
        this.owner = data.owner;
        this.thirdParties = data.thirdParties;
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.isFetchingContacts = false;
    },
    close() {
      this.$emit("close");
    },
    toggleAll() {
      this.selectedRecipients = this.allSelected
        ? []
        : this.allContacts.map((c) => c.id);
    },
    async relaunchFolder() {
      this.isSubmiting = true;
      try {
        await postFolderRemind({
          organizationId: this.$route.params.organizationId,
          folderId: this.$route.params.folderId,
          payload: {
            message: this.message,
            contacts: this.selectedRecipients,
          },
        });
        this.$emit("relaunch");
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.folder_relaunched_successfully"),
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.isSubmiting = false;
      this.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.modal_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  padding-top: 16px;
  flex-wrap: wrap;
  .modal_header_title {
    flex: 1;
  }
}
.modal_content {
  padding: 24px 10px;

  & > * {
    padding-bottom: 10px;
    &:last-child {
      padding-bottom: 0;
    }
  }
}
.item_section {
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  .section_header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .label {
    font-size: 12px;
    line-height: 16px;
    margin-bottom: 1em;
    color: #353542;
  }
  .desc {
    padding: 16px;
    background: #f9f9f9;
    border-radius: 5px;
    color: #707080;
  }

  .recipients_loading {
    display: flex;
    align-items: center;
    padding: 4px 0;
  }

  .contact_label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 2px 0;
  }
  .contact_identity {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
  }
  .contact_name {
    font-weight: 600;
    color: #353542;
    font-size: 13px;
  }
  .contact_email {
    color: #9090a0;
    font-size: 12px;
  }
  .contact_contexts {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  .contact_context_badge {
    font-size: 11px;
    color: #707080;
    background: #ebebf0;
    border-radius: 3px;
    padding: 1px 6px;
  }

}
.modal_footer {
  width: 100%;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  & > * {
    margin-left: 16px;
  }
}
</style>
